import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { AnimatePresence } from 'motion/react';
import { useKeyboardShortcut } from 'src/hooks';
import { sideDrawerBackdropMotion, sideDrawerSlideMotion } from 'src/components/SideDrawer/motion';
import StaticTable from 'src/apps/LearningSession/Common/BaseSession/LearningSessionHeader/StaticTable';
import * as S from './KeyboardShortcutDrawerStyles';

const msgs = defineMessages({
  title: { id: 'learning_session_keyboard_shortcuts_title' },
  headerKey: { id: 'learning_session_keyboard_shortcuts_key' },
  headerAction: { id: 'learning_session_keyboard_shortcuts_action' },
  next: { id: 'learning_session_keyboard_shortcuts_next' },
  replayMedia: { id: 'learning_session_keyboard_shortcuts_replay_media' },
  showShortcuts: {
    id: 'learning_session_keyboard_shortcuts_show_shortcuts',
  },
  navigation: { id: 'learning_session_keyboard_shortcuts_navigation' },
  backspace: { id: 'learning_session_keyboard_shortcuts_backspace' },
  numbers: { id: 'learning_session_keyboard_shortcuts_numbers' },
  close: { id: 'close' },
});

interface PortalProps {
  children: React.ReactNode;
}

const Portal = ({ children }: PortalProps) => {
  const domNode = document.querySelector('#modalPortalRoot');
  // Make the type system happy; the dom node is always there
  /* istanbul ignore next */
  if (!domNode) {
    throw new Error('Could not find modalPortalRoot');
  }

  return createPortal(children, domNode);
};

interface KeyboardShortcutDrawerProps {
  hide: () => void;
  isShown: boolean;
}

const KeyboardShortcutDrawer = ({ hide, isShown }: KeyboardShortcutDrawerProps) => {
  const intl = useIntl();
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  useFocus(modalContentRef, isShown);

  /* istanbul ignore next: default function */
  useKeyboardShortcut('esc', isShown ? hide : () => null);

  // To avoid the <Portal /> searching for a missing
  // #modalPortalRoot during SSR, we track whether the modal
  // has opened and return null early if it has not.
  //
  // We could use isShown for this, but we need to be able
  // to differentiate between the <Portal /> contents and
  // the <Portal /> itself so that exit animations on the
  // children work correctly (AnimatePresence still needs to
  // be in the DOM to make its children disappear correctly)
  const [hasOpened, setHasOpened] = useState(false);
  const titleText = intl.formatMessage(msgs.title);

  useEffect(() => {
    if (isShown && !hasOpened) {
      setHasOpened(true);
    }
  }, [isShown, setHasOpened, hasOpened]);

  if (!isShown && !hasOpened) return null;

  return (
    <Portal>
      <div ref={modalWrapperRef} role="dialog" aria-label={titleText} aria-modal="true">
        <AnimatePresence>
          {isShown && (
            <>
              <S.Backdrop
                key="keyboard-shortcut-drawer-backdrop"
                data-testid="keyboardShortcutDrawerBackdrop"
                onClick={hide}
                {...sideDrawerBackdropMotion}
              />
              <S.Root
                key="keyboard-shortcut-drawer"
                data-testid="keyboardShortcutDrawer"
                ref={modalContentRef}
                tabIndex={-1}
                {...sideDrawerSlideMotion}
              >
                <S.CloseButton
                  onClick={hide}
                  onKeyUp={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      hide();
                    }
                  }}
                  data-testid="keyboardShortcutDrawerCloseBtn"
                  aria-label={intl.formatMessage(msgs.close)}
                  tabIndex={0}
                >
                  <S.CloseIcon name="close" />
                </S.CloseButton>
                <S.Title>{titleText}</S.Title>
                <div>
                  <StaticTable
                    headerRow={[
                      intl.formatMessage(msgs.headerKey),
                      intl.formatMessage(msgs.headerAction),
                    ]}
                    rows={[
                      ['⏎', intl.formatMessage(msgs.next)],
                      ['r', intl.formatMessage(msgs.replayMedia)],
                      ['Ctrl/⌘ + /', intl.formatMessage(msgs.showShortcuts)],
                      ['←↓↑→ / hjkl', intl.formatMessage(msgs.navigation)],
                      [intl.formatMessage(msgs.backspace), intl.formatMessage(msgs.backspace)],
                      ['0 - 9', intl.formatMessage(msgs.numbers)],
                    ]}
                  />
                </div>
              </S.Root>
            </>
          )}
        </AnimatePresence>
      </div>
    </Portal>
  );
};

/**
 * Sets the focus on a given element when shouldFocus
 * changes to true
 *
 * The element should have tabIndex set if it's not
 * a naturally focusable element
 */
function useFocus<Element extends HTMLElement>(
  ref: React.RefObject<Element | null>,
  shouldFocus: boolean,
) {
  useEffect(() => {
    if (shouldFocus && ref && ref.current) {
      ref.current.focus();
    }
  }, [shouldFocus, ref]);
}

export default KeyboardShortcutDrawer;
